import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, getQueryKey } from '@feature/utils';

import * as api from './GovEvaluationApi';
import {
  GovAuthorEvaluation,
  GovEvalAuthorAnnexRequest,
  GovEvalHIndexRequest,
  GovEvalMinimalStandardRequest,
  GovEvalUserDataRequest,
  GovEvaluation,
} from './GovEvaluationTypes';

const evaluationKey = getQueryKey('gov-evaluation');

export function getGovStudyDomainsOptions(evaluationId: Id) {
  return {
    queryFn: () => api.getStudyDomains(evaluationId),
    queryKey: evaluationKey.as([evaluationId, 'study-domains']),
  };
}

export function getGovEvaluationsOptions(): UseSuspenseQueryOptions<GovEvaluation[]> {
  return {
    queryFn: () => api.getAll(),
    queryKey: evaluationKey.list(),
  };
}

export function getGovEvaluationOptions(evaluationId: Id): UseSuspenseQueryOptions<GovEvaluation> {
  return {
    queryFn: () => api.get(evaluationId),
    queryKey: evaluationKey.one(evaluationId),
  };
}

export function getGovAuthorEvaluationOptions(
  evaluationId: Id,
  authorId: Id,
): UseSuspenseQueryOptions<GovAuthorEvaluation | null> {
  return {
    queryFn: () => api.getForAuthor(evaluationId, authorId),
    queryKey: evaluationKey.as([evaluationId, 'author', authorId]),
  };
}

export function useGovEvaluationOpen(year: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.open(year),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useGovEvaluationStart(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.start(evaluationId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useGovEvaluationUploadAnnex(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: GovEvalAuthorAnnexRequest) => api.updateAnnex(evaluationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useGovEvaluationUpdateUserData(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: GovEvalUserDataRequest) => api.updateUserData(evaluationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useGovEvaluationComplete(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.complete(evaluationId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useUpdateHIndex(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: GovEvalHIndexRequest) => api.updateHIndex(evaluationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useGovEvalMinStdDelete(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteMinimalStandards(evaluationId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}

export function useUpdateMinimalStandard(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: GovEvalMinimalStandardRequest) => api.updateMinimalStandard(evaluationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: evaluationKey.root(),
      }),
  });
}
